// src/components/HomeNavbar.jsx
import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import Badge from "@mui/material/Badge";
import { useNavigate, useLocation } from "react-router-dom";

/* ---------------- Styled Components ---------------- */

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 100px;
  background-color: #fff;
  z-index: 1000;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  padding-left: 5px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media only screen and (max-width: 768px) {
    height: 60px;
    padding-left: 0;
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 160px 10px 20px;
  gap: 25px;

  @media only screen and (max-width: 768px) {
    padding: 10px 125px 10px 15px;
    justify-content: space-between;
    gap: 10px;
  }
`;

/* Desktop menu — hidden on mobile */
const DesktopMenu = styled.div`
  display: flex;
  align-items: center;
  gap: 25px;
  flex: 1;

  @media only screen and (max-width: 768px) {
    display: none;
  }
`;

/* Hamburger button — hidden on desktop */
const HamburgerButton = styled.div`
  display: none;
  cursor: pointer;
  padding: 6px;

  @media only screen and (max-width: 768px) {
    display: flex;
    align-items: center;
  }
`;

/* Mobile logo/title area */
const MobileTitle = styled.div`
  display: none;
  font-weight: bold;
  font-size: 18px;
  color: teal;

  @media only screen and (max-width: 768px) {
    display: block;
  }
`;

/* Mobile cart — stays visible on mobile top row */
const MobileCart = styled.div`
  display: none;

  @media only screen and (max-width: 768px) {
    display: flex;
    align-items: center;
  }
`;

/* Mobile drawer that slides down with all menu items */
const MobileDrawer = styled.div`
  display: none;

  @media only screen and (max-width: 768px) {
    display: ${(props) => (props.open ? "flex" : "none")};
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    background: #fff;
    flex-direction: column;
    padding: 10px 0;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
    z-index: 999;
    max-height: calc(100vh - 60px);
    overflow-y: auto;
  }
`;

const BottomRow = styled.div`
  padding: 0 20px 10px 20px;

  @media only screen and (max-width: 768px) {
    display: none;
  }
`;

const MenuItem = styled.div`
  font-size: 14px;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 6px;
  transition: background 0.3s ease;
  &:hover {
    background-color: #f0f0f0;
  }
  color: ${(props) => (props.greeting ? "teal" : "inherit")};

  @media only screen and (max-width: 768px) {
    font-size: 16px;
    padding: 12px 20px;
    border-radius: 0;
    border-bottom: 1px solid #eee;
  }
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
  background-color: #f1f1f1;
  border-radius: 25px;
  padding: 6px 12px;
`;

const Input = styled.input`
  border: none;
  width: 100%;
  font-size: 16px;
  background: transparent;
  outline: none;
  padding-left: 10px;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

/* ---------------- Component ---------------- */

export default function HomeNavbar(props) {
  console.log("NAVBAR RENDERED");
  console.log("Navbar props:", props);

  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const fileInputRef = useRef(null);

  const openCadUpload = () => {
    console.log("AutoCAD clicked");
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setDrawerOpen(false);
    navigate("/", { replace: true });
  };

  // Wraps navigate() so clicking an item on mobile also closes the drawer.
  const go = (path) => {
    setDrawerOpen(false);
    navigate(path);
  };

  useEffect(() => {
    const u = JSON.parse(localStorage.getItem("user"));
    setUser(u);
  }, [location.pathname]);


  // Shared menu items — rendered twice (desktop row + mobile drawer).
  const menuItems = (
    <>
      <MenuItem onClick={() => go("/")}>Home</MenuItem>

      {/*<MenuItem onClick={() => go("/car-design")}>Car Design</MenuItem>*/}

      <MenuItem onClick={() => go("/stocks")}>Stocks</MenuItem>

      {/*<MenuItem onClick={() => go("/traders")}>Traders</MenuItem>*/}

      {/*<MenuItem
        onClick={() => {
          setDrawerOpen(false);
          openCadUpload();
        }}
      >
        AutoCAD
      </MenuItem>*/}

      {/* <MenuItem
        onClick={() => {
          setDrawerOpen(false);
          props.onOpenQuantities && props.onOpenQuantities();
        }}
      >
        Quantities
      </MenuItem>*/}

      <MenuItem onClick={() => go("/gis")}>GIS</MenuItem>

      {/* <MenuItem onClick={() => go("/building-designer")}>
        Building Designer
      </MenuItem>*/}

      {user ? (
        <>
          <MenuItem greeting>Hello, {user.name}</MenuItem>
          <MenuItem onClick={() => go("/dashboard")}>Dashboard</MenuItem>
          <MenuItem onClick={handleLogout}>Logout</MenuItem>
        </>
      ) : (
        <>
          <MenuItem onClick={() => go("/register")}>Register</MenuItem>
          <MenuItem onClick={() => go("/login")}>Sign In</MenuItem>
        </>
      )}
    </>
  );

  return (
    <>
      {/* Hidden DXF input OUTSIDE the fixed navbar */}
      <input
        type="file"
        accept=".dxf,.dwg"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={async (e) => {
          console.log("DXF selected");
          await props.onCadUpload(e);
          e.target.value = "";
        }}
      />

      <Container>
        <ContentWrapper>
          <TopRow>
            {/* Mobile: hamburger + title + cart */}
            <HamburgerButton onClick={() => setDrawerOpen((o) => !o)}>
              {drawerOpen ? <CloseIcon /> : <MenuIcon />}
            </HamburgerButton>

            <MobileTitle onClick={() => go("/")}>SaudiDunes</MobileTitle>

            <MobileCart>
              <Badge badgeContent={4} color="primary">
                <ShoppingCartOutlinedIcon />
              </Badge>
            </MobileCart>

            {/* Desktop: all items in one row (unchanged look) */}
            <DesktopMenu>
              {menuItems}
              <MenuItem>
                <Badge badgeContent={4} color="primary">
                  <ShoppingCartOutlinedIcon />
                </Badge>
              </MenuItem>
            </DesktopMenu>
          </TopRow>

          <BottomRow>
            <SearchContainer>
              <SearchIcon style={{ color: "gray", fontSize: 20 }} />
              <Input placeholder="Search..." />
            </SearchContainer>
          </BottomRow>
        </ContentWrapper>

        {/* Mobile drawer with full menu */}
        <MobileDrawer open={drawerOpen}>{menuItems}</MobileDrawer>
      </Container>
    </>
  );
}
